import { createBrowserRouter, Navigate, type RouteObject } from 'react-router';

import Layout from './components/Layout/Layout';
import { OAuthCallbackPage } from './features/auth';
import { isLandingHost } from './lib/landingHost';
import Index from './pages/Index';
import Landing from './pages/Landing';
import Privacy from './pages/legal/Privacy';
import Terms from './pages/legal/Terms';
import LiveTrades from './pages/LiveTrades';
import Notfound from './pages/Notfound';

/**
 * The landing section. Mounted at `/landing` on app.snappy.trade and at `/`
 * on snappy.trade (see lib/landingHost.ts), so a page added here is reachable
 * as app.snappy.trade/landing/<x> and snappy.trade/<x> with no other change.
 */
const landingChildren: RouteObject[] = [
    {
        index: true,
        element: (
            <Layout>
                <Landing />
            </Layout>
        ),
    },
];

const legalRoutes: RouteObject[] = [
    {
        path: '/terms',
        element: (
            <Layout>
                <Terms />
            </Layout>
        ),
    },
    {
        path: '/privacy',
        element: (
            <Layout>
                <Privacy />
            </Layout>
        ),
    },
];

const notFoundRoute: RouteObject = {
    path: '*',
    element: (
        <Layout>
            <Notfound />
        </Layout>
    ),
};

export function buildRoutes(onLandingHost: boolean): RouteObject[] {
    if (onLandingHost) {
        // Only the landing section and the legal pages it links to exist on the
        // apex; the terminal and everything else live on app.snappy.trade.
        return [
            { path: '/', children: landingChildren },
            ...legalRoutes,
            notFoundRoute,
        ];
    }

    return [
        {
            path: '/',
            element: (
                <Layout>
                    <Index />
                </Layout>
            ),
        },
        {
            // The public tape. Its own route rather than a tab in the terminal
            // table: it is everyone's trades, not yours, and it needs the room.
            path: '/live',
            element: (
                <Layout>
                    <LiveTrades />
                </Layout>
            ),
        },
        { path: '/landing', children: landingChildren },
        {
            // The terminal briefly lived at /trade — keep shared links working.
            path: '/trade',
            element: <Navigate to="/" replace />,
        },
        {
            // OAuth redirect target for v2-auth social login (see features/auth).
            path: '/auth/callback',
            element: <OAuthCallbackPage />,
        },
        ...legalRoutes,
        notFoundRoute,
    ];
}

const router = createBrowserRouter(buildRoutes(isLandingHost()));

export default router;
